// =============================================================
//  Charts — Wrappers recharts (LineChart, BarChart, PieChart, AreaChart)
//  Pour le rendu en React Native Web, recharts s'utilise tel quel.
// =============================================================

import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
} from 'recharts';
import { Card } from './kit';
import { colors, radius, spacing } from './theme';

const tooltipStyle = {
  backgroundColor: '#fff',
  border: `1px solid ${colors.border}`,
  borderRadius: 8,
  fontSize: 12,
  padding: 8,
};

// ---------- ChartCard ----------
// Wrapper unifié pour titrer/encadrer un graphique.
export const ChartCard = ({ title, subtitle, action, children, height = 260, style }) => (
  <Card style={[{ minHeight: height + 80 }, style]}>
    <View style={styles.cardHeader}>
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={styles.cardTitle}>{title}</Text>
        {!!subtitle && <Text style={styles.cardSub}>{subtitle}</Text>}
      </View>
      {!!action && <View>{action}</View>}
    </View>
    <View style={{ height, marginTop: spacing.md }}>{children}</View>
  </Card>
);

// ---------- AreaSpark (courbe d'évolution avec dégradé) ----------
export const AreaSpark = ({ data, dataKey = 'value', xKey = 'label', color = colors.primary }) => (
  <ResponsiveContainer width="100%" height="100%">
    <AreaChart data={data} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
      <defs>
        <linearGradient id="aspark" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.35} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <CartesianGrid strokeDasharray="3 3" stroke={colors.border} vertical={false} />
      <XAxis
        dataKey={xKey}
        tick={{ fill: colors.textMuted, fontSize: 11 }}
        axisLine={false}
        tickLine={false}
      />
      <YAxis
        tick={{ fill: colors.textMuted, fontSize: 11 }}
        axisLine={false}
        tickLine={false}
        width={36}
      />
      <Tooltip contentStyle={tooltipStyle} />
      <Area
        type="monotone"
        dataKey={dataKey}
        stroke={color}
        strokeWidth={2.5}
        fill="url(#aspark)"
      />
    </AreaChart>
  </ResponsiveContainer>
);

// ---------- BarSimple ----------
export const BarSimple = ({ data, dataKey = 'value', xKey = 'label', color = colors.primary }) => (
  <ResponsiveContainer width="100%" height="100%">
    <BarChart data={data} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
      <CartesianGrid strokeDasharray="3 3" stroke={colors.border} vertical={false} />
      <XAxis
        dataKey={xKey}
        tick={{ fill: colors.textMuted, fontSize: 11 }}
        axisLine={false}
        tickLine={false}
      />
      <YAxis
        tick={{ fill: colors.textMuted, fontSize: 11 }}
        axisLine={false}
        tickLine={false}
        width={36}
      />
      <Tooltip contentStyle={tooltipStyle} cursor={{ fill: colors.surfaceAlt }} />
      <Bar dataKey={dataKey} fill={color} radius={[6, 6, 0, 0]} maxBarSize={36} />
    </BarChart>
  </ResponsiveContainer>
);

// ---------- PieDonut ----------
export const PieDonut = ({ data, dataKey = 'value', nameKey = 'label', palette }) => {
  const pal = palette || colors.chart;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie
          data={data}
          dataKey={dataKey}
          nameKey={nameKey}
          innerRadius={55}
          outerRadius={90}
          paddingAngle={2}
          stroke="none"
        >
          {data.map((_, i) => (
            <Cell key={i} fill={pal[i % pal.length]} />
          ))}
        </Pie>
        <Tooltip contentStyle={tooltipStyle} />
        <Legend
          iconType="circle"
          verticalAlign="bottom"
          height={36}
          wrapperStyle={{ fontSize: 12, color: colors.textSecondary }}
        />
      </PieChart>
    </ResponsiveContainer>
  );
};

// ---------- LineDouble (comparaison 2 séries) ----------
export const LineDouble = ({
  data,
  keys = ['a', 'b'],
  labels = ['Série A', 'Série B'],
  colors: lineColors = [colors.primary, colors.success],
  xKey = 'label',
}) => (
  <ResponsiveContainer width="100%" height="100%">
    <LineChart data={data} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
      <CartesianGrid strokeDasharray="3 3" stroke={colors.border} vertical={false} />
      <XAxis
        dataKey={xKey}
        tick={{ fill: colors.textMuted, fontSize: 11 }}
        axisLine={false}
        tickLine={false}
      />
      <YAxis
        tick={{ fill: colors.textMuted, fontSize: 11 }}
        axisLine={false}
        tickLine={false}
        width={36}
      />
      <Tooltip contentStyle={tooltipStyle} />
      <Legend wrapperStyle={{ fontSize: 12 }} />
      {keys.map((k, i) => (
        <Line
          key={k}
          name={labels[i]}
          type="monotone"
          dataKey={k}
          stroke={lineColors[i]}
          strokeWidth={2.5}
          dot={{ r: 3 }}
          activeDot={{ r: 5 }}
        />
      ))}
    </LineChart>
  </ResponsiveContainer>
);

const styles = StyleSheet.create({
  cardHeader: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  cardTitle: { fontSize: 15, fontWeight: '700', color: colors.text },
  cardSub: { fontSize: 12, color: colors.textSecondary, marginTop: 3 },
});
